// src/components/Customer/CustomerDetailsModal.jsx
import React from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Badge,
  Placeholder,
  Tab,
  Nav,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { MapPin, Briefcase } from "lucide-react";
import { formatDateTime } from "../../utils/formatters";

const safe = (val, placeholder = "—") =>
  val === null || val === undefined || val === "" ? placeholder : val;

const getInitials = (name) => {
  if (!name) return "NA";
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || "";
  const second = parts[1]?.[0] || "";
  const initials = (first + second).toUpperCase();
  return initials || "NA";
};

const DlItem = ({ label, value }) => (
  <Row className="py-2 border-bottom align-items-center">
    <Col md={4} className="text-muted small fw-semibold text-uppercase">
      {label}
    </Col>
    <Col md={8} className="fw-normal text-success">
      {value}
    </Col>
  </Row>
);

const LoadingBlock = () => (
  <div>
    {[...Array(6)].map((_, i) => (
      <Row className="py-2" key={i}>
        <Col md={4}>
          <Placeholder animation="wave">
            <Placeholder xs={6} />
          </Placeholder>
        </Col>
        <Col md={8}>
          <Placeholder animation="wave">
            <Placeholder xs={10} />
          </Placeholder>
        </Col>
      </Row>
    ))}
  </div>
);

const RoleBadges = ({ roles }) => {
  if (!Array.isArray(roles) || roles.length === 0)
    return <span className="text-muted">—</span>;

  const show = roles.slice(0, 3);
  const extra = roles.length - show.length;

  return (
    <div className="d-flex flex-wrap gap-2">
      {show.map((id) => (
        <Badge key={id} pill bg="light" className="text-dark border">
          {id}
        </Badge>
      ))}
      {extra > 0 && (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>{roles.join(", ")}</Tooltip>}
        >
          <Badge pill bg="secondary">
            +{extra}
          </Badge>
        </OverlayTrigger>
      )}
    </div>
  );
};

/**
 * Props:
 * - visible: bool
 * - onClose: fn
 * - data: CustomerDetailDto
 * - loading: bool
 */
const CustomerDetailsModal = ({
  visible,
  onClose,
  data = {},
  loading = false,
}) => {
  const isActive = !!data?.isActive;

  // Nombre “maestro” para header/avatar: DisplayName > FullName > CompanyName
  const displayName =
    data?.displayName || data?.fullName || data?.companyName || "Cliente";

  const avatarLabel =
    data?.displayName || data?.fullName || data?.companyName || "NA";

  return (
    <Modal show={visible} onHide={onClose} size="lg" centered scrollable>
      {/* HEADER */}
      <div className="d-flex align-items-center gap-3 p-4 lawyer-header">
        <div
          className="rounded-circle shadow-sm d-flex align-items-center justify-content-center border"
          style={{
            width: 70,
            height: 70,
            background: "var(--bs-body-bg)",
            fontSize: "1.5rem",
            color: "#0d6efd",
            fontWeight: 600,
          }}
        >
          {getInitials(avatarLabel)}
        </div>
        <div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <h4 className="mb-0 fw-semibold text-body">{safe(displayName)}</h4>
            <Badge
              bg={isActive ? "success" : "danger"}
              pill
              className="text-light fw-semibold"
            >
              {isActive ? "Activo" : "Inactivo"}
            </Badge>
          </div>

          {/* Email de contacto (no necesariamente del SecurityUser) */}
          <div className="text-muted small mt-1">{safe(data?.email)}</div>

          {/* Empresa tenant (firma que usa NovianCore) */}
          {data?.tenantCompanyName && (
            <div className="small mt-1">
              <span className="text-muted">Empresa:</span>{" "}
              <span className="fw-semibold">{data.tenantCompanyName}</span>
            </div>
          )}

          {/* Tipo de cliente (Natural / Jurídica) */}
          {data?.customerTypeName && (
            <div className="small mt-1">
              <span className="text-muted">Tipo de cliente:</span>{" "}
              <span className="fw-semibold">{data.customerTypeName}</span>
            </div>
          )}
        </div>
      </div>

      {/* BODY */}
      <Modal.Body className="pt-3 px-4">
        <Tab.Container defaultActiveKey="resumen">
          <Nav
            variant="pills"
            className="mb-4 flex-wrap gap-2 justify-content-start"
          >
            <Nav.Item>
              <Nav.Link eventKey="resumen">Resumen</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="personal">Personal</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="direccion">Dirección</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="comercial">Comercial / Fiscal</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="sistema">Sistema</Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* === RESUMEN === */}
            <Tab.Pane eventKey="resumen">
              {loading ? (
                <LoadingBlock />
              ) : (
                <div className="p-3 rounded-3 shadow-sm bg-body-tertiary">
                  <Row className="gy-3">
                    <Col md={6}>
                      <DlItem
                        label="Documento"
                        value={
                          <>
                            {safe(data?.documentTypeName)}{" "}
                            {safe(data?.documentNumber)}
                          </>
                        }
                      />
                    </Col>
                    <Col md={6}>
                      <DlItem
                        label="Tipo de cliente"
                        value={safe(data?.customerTypeName)}
                      />
                    </Col>
                    <Col md={6}>
                      <DlItem label="Celular" value={safe(data?.phoneMobile)} />
                    </Col>
                    <Col md={6}>
                      <DlItem label="Correo" value={safe(data?.email)} />
                    </Col>
                    <Col md={6}>
                      <DlItem
                        label="Fecha nacimiento"
                        value={
                          data?.birthDate ? formatDateTime(data.birthDate) : "—"
                        }
                      />
                    </Col>
                    <Col md={6}>
                      <DlItem
                        label="Razón social"
                        value={safe(data?.companyName)}
                      />
                    </Col>
                  </Row>
                </div>
              )}
            </Tab.Pane>

            {/* === PERSONAL === */}
            <Tab.Pane eventKey="personal">
              {loading ? (
                <LoadingBlock />
              ) : (
                <div className="p-3 rounded-3 shadow-sm bg-body-tertiary">
                  <DlItem label="Nombres" value={safe(data?.firstName)} />
                  <DlItem label="Apellidos" value={safe(data?.lastName)} />
                  <DlItem
                    label="Tipo documento"
                    value={safe(data?.documentTypeName)}
                  />
                  <DlItem
                    label="Número documento"
                    value={safe(data?.documentNumber)}
                  />
                  <DlItem
                    label="Fecha nacimiento"
                    value={
                      data?.birthDate ? formatDateTime(data.birthDate) : "—"
                    }
                  />
                  <DlItem label="Género" value={safe(data?.genderName)} />
                  <DlItem
                    label="Teléfono móvil"
                    value={safe(data?.phoneMobile)}
                  />
                  <DlItem
                    label="Teléfono alterno"
                    value={safe(data?.phoneAlt)}
                  />
                </div>
              )}
            </Tab.Pane>

            {/* === DIRECCIÓN === */}
            <Tab.Pane eventKey="direccion">
              {loading ? (
                <LoadingBlock />
              ) : (
                <div className="p-3 rounded-3 shadow-sm bg-body-tertiary">
                  <h6 className="mb-3 text-primary d-flex align-items-center gap-2">
                    <MapPin size={16} /> Ubicación
                  </h6>
                  <DlItem
                    label="Dirección 1"
                    value={safe(data?.addressLine1)}
                  />
                  <DlItem
                    label="Dirección 2"
                    value={safe(data?.addressLine2)}
                  />
                  <DlItem label="País" value={safe(data?.countryName)} />
                  <DlItem
                    label="Departamento"
                    value={safe(data?.departmentName)}
                  />
                  <DlItem
                    label="Municipio"
                    value={safe(data?.municipalityName)}
                  />
                </div>
              )}
            </Tab.Pane>

            {/* === COMERCIAL / FISCAL === */}
            <Tab.Pane eventKey="comercial">
              {loading ? (
                <LoadingBlock />
              ) : (
                <div className="p-3 rounded-3 shadow-sm bg-body-tertiary">
                  <h6 className="mb-3 text-primary d-flex align-items-center gap-2">
                    <Briefcase size={16} /> Información comercial / fiscal
                  </h6>
                  <DlItem
                    label="Razón social"
                    value={safe(data?.companyName)}
                  />
                  <DlItem label="NIT" value={safe(data?.taxId)} />
                  <DlItem label="DV" value={safe(data?.taxVerificationDigit)} />
                  <DlItem
                    label="Actividad económica"
                    value={safe(data?.businessActivity)}
                  />
                  <DlItem label="Notas" value={safe(data?.notes)} />
                </div>
              )}
            </Tab.Pane>

            {/* === SISTEMA === */}
            <Tab.Pane eventKey="sistema">
              {loading ? (
                <LoadingBlock />
              ) : (
                <div className="p-3 rounded-3 shadow-sm bg-body-tertiary">
                  <DlItem label="ID" value={safe(data?.id)} />
                  <DlItem
                    label="SecurityUserId"
                    value={safe(data?.securityUserId)}
                  />
                  <DlItem label="CompanyId" value={safe(data?.companyId)} />
                  <DlItem
                    label="Empresa (tenant)"
                    value={safe(data?.tenantCompanyName)}
                  />
                  <DlItem
                    label="CustomerTypeId"
                    value={safe(data?.customerTypeId)}
                  />
                  <DlItem
                    label="DocumentTypeId"
                    value={safe(data?.documentTypeId)}
                  />
                  <DlItem
                    label="Roles asignados"
                    value={<RoleBadges roles={data?.roleIds} />}
                  />
                  <DlItem
                    label="Creado"
                    value={
                      data?.createdAt ? formatDateTime(data.createdAt) : "—"
                    }
                  />
                  <DlItem
                    label="Actualizado"
                    value={
                      data?.updatedAt ? formatDateTime(data.updatedAt) : "—"
                    }
                  />
                </div>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Modal.Body>

      {/* FOOTER */}
      <Modal.Footer className="border-0 px-4">
        <Button variant="secondary" onClick={onClose}>
          Cerrar
        </Button>
      </Modal.Footer>

      {/* Estilos adaptativos */}
      <style jsx global>{`
        .lawyer-header {
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          border-bottom: 1px solid var(--bs-border-color);
        }
        [data-bs-theme="dark"] .lawyer-header {
          background: linear-gradient(135deg, #1e1e1e 0%, #2b2b2b 100%);
        }
        [data-bs-theme="light"] {
          --table-head-bg: #f1f3f5;
          --table-head-text: #212529;
        }
        [data-bs-theme="dark"] {
          --table-head-bg: #222;
          --table-head-text: #8ce99a;
        }
      `}</style>
    </Modal>
  );
};

export default CustomerDetailsModal;
